import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, FileText, FolderKanban, GraduationCap, BookOpen, ShieldCheck, School } from "lucide-react";
import api from "../lib/api";

const ICONS = { page: FileText, project: FolderKanban, course: GraduationCap, story: BookOpen, reference: ShieldCheck, training_partner: School };
const SUGGESTIONS = ["Housing", "Borehole", "School fees", "Training", "Solar", "Food", "Emergency", "Donation", "Projects"];

export const SiteSearch = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
    setTimeout(() => inputRef.current?.focus(), 50);
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data.results); setActive(0); } catch { setResults([]); } finally { setBusy(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (r) => { onClose(); nav(r.path); };
  const onKey = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && results[active]) go(results[active]);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-[#155e3f]/80 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4" onClick={onClose} data-testid="site-search">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-black/10">
          <Search size={20} className="text-[#d6aa43]" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search housing, borehole, school fees, training, solar, food, emergency…" className="flex-1 text-base outline-none bg-transparent" data-testid="site-search-input" aria-label="Search the website" />
          <button onClick={onClose} aria-label="Close search" data-testid="site-search-close"><X size={20} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="site-search-results">
          {q.trim().length < 2 && (
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#0a0d12]/60 font-bold mb-3">Popular searches</div>
              <div className="flex flex-wrap gap-2">{SUGGESTIONS.map((s) => <button key={s} onClick={() => setQ(s)} className="px-3 py-1.5 rounded-full bg-[#f8f4e8] text-sm hover:bg-[#d6aa43]/30" data-testid={`search-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</button>)}</div>
            </div>
          )}
          {q.trim().length >= 2 && !busy && results.length === 0 && <div className="p-6 text-sm text-[#0a0d12]/60" data-testid="site-search-empty">No results for “{q}”. Try “housing”, “borehole” or “school fees”, or <button className="underline" onClick={() => go({ path: "/get-help" })}>visit Get Help</button>.</div>}
          {results.map((r, i) => { const Icon = ICONS[r.type] || FileText; return (
            <button key={`${r.path}-${r.title || r.label || ""}`} onMouseEnter={() => setActive(i)} onClick={() => go(r)} className={`w-full text-left flex items-start gap-3 px-5 py-3 border-b border-black/5 ${i === active ? "bg-[#f8f4e8]" : "hover:bg-[#f8f4e8]/60"}`} data-testid={`search-result-${i}`}>
              <Icon size={18} className="text-[#0d1724] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-[#0d1724]">{r.title}</div><div className="text-xs text-[#0a0d12]/60 truncate">{r.description}</div></div>
              <span className="text-[10px] uppercase tracking-wider text-[#0a0d12]/40 mt-1 capitalize">{r.type.replace("_", " ")}</span>
              <ArrowRight size={14} className="text-[#d6aa43] mt-1" />
            </button>
          ); })}
        </div>
        <div className="px-5 py-2 text-[10px] text-[#0a0d12]/40 border-t border-black/5 flex justify-between"><span>↑↓ to navigate · Enter to open · Esc to close</span><span>Dutsen Kauna Foundation</span></div>
      </div>
    </div>
  );
};
